import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { SubrecordTable } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import { SubrecordTableProps } from "containers/Resources/screens/ResourcesSingle/components/UserGitPublicKeyList";
import { Plugin } from "graphql/generated/graphql";
import { useCallback, useMemo } from "react";
import { getRelativeUrl } from "utils/service";

// localStorage tableSettings key, so page size / sort order persist (parity with
// other record lists). Client-side list: base order is unset (no default sort).
const pluginActionListKey = "pluginActionList";
const pluginActionInitialQuery = { limit: 10, order: "", offset: 0 };

export const PluginActionList = ({
    resourceData,
    onResourceClick,
    createSubrecord,
    fetchResourceData,
    isFieldDisabled,
}: SubrecordTableProps<Plugin>) => {
    const pluginData = useMemo(
        () =>
            resourceData?.plugin_action_plain_list_plugin_id?.map((pluginAction) => ({
                ...pluginAction,
                actionResourceType: pluginAction?.record_type ?? "not selected",
                resource_type: "plugin_action",
            })),
        [resourceData]
    );
    const openPluginActionForm = useCallback(
        (e, id) =>
            onResourceClick({
                e,
                id,
                resource_type: "plugin_action",
            }),
        [onResourceClick]
    );
    const columns = [
        {
            label: "Record type",
            key: "record_type",
            shown: true,
            sortable: true,
            dto: ({ id, actionResourceType }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl("plugin_action", id)}
                        name={actionResourceType}
                        onClick={(e) => openPluginActionForm(e, id)}
                    />
                );
            },
        },
        {
            label: "Name",
            key: "name",
            shown: true,
            sortable: true,
            dto: ({ name, id }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl("plugin_action", id)}
                        name={name}
                        onClick={(e) => openPluginActionForm(e, id)}
                    />
                );
            },
        },
        {
            label: "Enabled",
            key: "is_enabled",
            shown: true,
            sortable: true,
            dto: ({ is_enabled, id }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl("plugin_action", id)}
                        icon={is_enabled ? IconCollection.check : undefined}
                        onClick={(e) => openPluginActionForm(e, id)}
                    />
                );
            },
        },
    ];

    return (
        <SubrecordTable
            key="pluginActionList"
            data={pluginData as any[]}
            uniqueKey="pluginActionList"
            title="Plugin actions"
            onAddClick={createSubrecord}
            isActionButtonDisabled={isFieldDisabled}
            refreshData={fetchResourceData}
            columns={columns}
            emptyListText="No plugin actions."
            recordLabel="plugin action"
            initialQuery={getInitialQueryWithTableSettings(
                pluginActionListKey,
                pluginActionInitialQuery
            )}
            tableSettingsKey={pluginActionListKey}
            tableOptions={{ viewColumns: true }}
        />
    );
};
